'use client';

/**
 * Plan-details card for the credit usage overlay — one of three shapes
 * depending on how the workspace's credits are funded.
 *
 * Mantine components used: `Paper`, `Stack`, `Group`, `Text`, `Box`.
 */

import { faCoins } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Group, Paper, Stack, Text } from '@mantine/core';
import type { ReactNode } from 'react';
import { tokens } from 'ui-lib/src/lib/css/tokens';

import type { CreditPlanKind, CreditSummary } from './derive-credit-summary-view';
import { formatCreditCount, formatDate, formatUsd, toCredits } from './format';
import { CREDIT_PRICING } from './pricing';
import { chargeBlock, planCard, planCardSection, ruledDetailRow, statRow } from './styles.css';

const ADDITIONAL_CHARGE_CAPTION = 'Billed at month end, confirmed on your invoice';

interface CreditUsagePlanCardProps {
  planKind: CreditPlanKind;
  summary: CreditSummary;
  purchasedMilli: number;
  consumedFromPacksMilli: number;
  availableMilli: number;
  renewalDate: string | null;
}

/**
 * One label/value line under a hairline rule. `emphasis` is for a card whose
 * only stat is the month's usage, where the line carries the card rather than
 * sitting in a list of them.
 */
function StatRow({
  label,
  value,
  emphasis = false,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  const weight = emphasis ? 600 : undefined;

  return (
    <Box className={statRow}>
      <Group justify="space-between" wrap="nowrap" gap="sm">
        <Text size="sm" fw={weight}>
          {label}
        </Text>
        <Text size="sm" fw={weight}>
          {value}
        </Text>
      </Group>
    </Box>
  );
}

function CoinRow({ label, value }: { label: string; value: string | null }) {
  return (
    <Group justify="space-between" wrap="nowrap" gap="sm">
      <Group gap="xs" wrap="nowrap">
        <FontAwesomeIcon icon={faCoins} size="sm" />
        <Text size="sm" fw={600}>
          {label}
        </Text>
      </Group>
      {value !== null && (
        <Text size="sm" fw={600}>
          {value}
        </Text>
      )}
    </Group>
  );
}

/** A secondary line in the design's tertiary ink: 12px on a 20px line. */
function DetailText({ children, pb }: { children: ReactNode; pb?: number }) {
  return (
    <Text size="xs" lh="20px" c={tokens.color.foreground25.contentTertiary} pb={pb}>
      {children}
    </Text>
  );
}

/** A compact label/value detail under a card stat; `ruled` draws the hairline beneath it. */
function DetailRow({
  label,
  value,
  ruled = false,
}: {
  label: string;
  value: string;
  ruled?: boolean;
}) {
  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      gap="sm"
      className={ruled ? ruledDetailRow : undefined}
    >
      <DetailText>{label}</DetailText>
      <DetailText>{value}</DetailText>
    </Group>
  );
}

function RenewalDate({ renewalDate }: { renewalDate: string | null }) {
  if (renewalDate === null) {
    return null;
  }

  return (
    <Group gap={4} wrap="nowrap">
      <Text size="sm" lh="20px" c={tokens.color.foreground25.contentTertiary}>
        Renewal date
      </Text>
      <Text size="sm" lh="20px" fw={600}>
        {formatDate(renewalDate)}
      </Text>
    </Group>
  );
}

function ChargeBlock({
  label,
  amountUsd,
  caption,
}: {
  label: string;
  amountUsd: number;
  caption: string;
}) {
  return (
    <Box className={chargeBlock}>
      <Group justify="space-between" wrap="nowrap" gap="sm">
        <Text fw={600} size="sm" lh="16px">
          {label}
        </Text>
        <Text fw={600} size="lg" lh="28px">
          {formatUsd(amountUsd)}
        </Text>
      </Group>
      <DetailText>{caption}</DetailText>
    </Box>
  );
}

function PlanCardShell({ children }: { children: ReactNode }) {
  return (
    // Grows to the height of the breakdown table beside it, with its blocks spread
    // over that height rather than bunched at the top.
    <Paper shadow="none" radius="xlarge" p={20} className={planCard} flex="1 1 auto">
      <Stack gap="sm" h="100%" justify="space-between">
        {children}
      </Stack>
    </Paper>
  );
}

function CreditPacksCard({
  summary,
  purchasedMilli,
  consumedFromPacksMilli,
  availableMilli,
  renewalDate,
}: {
  summary: CreditSummary;
  purchasedMilli: number;
  consumedFromPacksMilli: number;
  availableMilli: number;
  renewalDate: string | null;
}) {
  return (
    <PlanCardShell>
      <Group justify="space-between" gap="sm">
        <Text fw={600}>Annual purchase</Text>
        <RenewalDate renewalDate={renewalDate} />
      </Group>
      <CoinRow label="Credit usage" value={null} />
      <Box>
        <StatRow label="Purchased credits" value={formatCreditCount(purchasedMilli)} />
        <StatRow label="Credits consumed" value={formatCreditCount(consumedFromPacksMilli)} />
        <StatRow label="Credits available" value={formatCreditCount(availableMilli)} />
      </Box>
      <ChargeBlock
        label="Estimated additional charge"
        amountUsd={toCredits(summary.estimatedBillMilli) * CREDIT_PRICING.paygUsdPerCredit}
        caption={ADDITIONAL_CHARGE_CAPTION}
      />
    </PlanCardShell>
  );
}

function UnlimitedCard({
  summary,
  purchasedMilli,
  renewalDate,
}: {
  summary: CreditSummary;
  purchasedMilli: number;
  renewalDate: string | null;
}) {
  const bulkEnrichmentMilli =
    summary.breakdown.bulkEnrichment.emailsMilli +
    summary.breakdown.bulkEnrichment.phoneNumbersMilli +
    summary.breakdown.bulkEnrichment.companyInfoMilli;
  const packageCaption =
    purchasedMilli > 0
      ? `Unlimited package + ${formatCreditCount(purchasedMilli)} Credits`
      : 'Unlimited package';

  return (
    // Figma `2413:11488`: title and package line, then the usage block under a
    // hairline with its two compact details, then the charge.
    <PlanCardShell>
      <Box>
        <Group justify="space-between" wrap="nowrap" gap="sm">
          <Text fw={600} size="lg" lh="28px">
            Unlimited usage
          </Text>
          <RenewalDate renewalDate={renewalDate} />
        </Group>
        <DetailText pb={4}>{packageCaption}</DetailText>
      </Box>
      <Stack gap={6} className={planCardSection}>
        <CoinRow label="Credits used" value={formatCreditCount(summary.consumedThisMonthMilli)} />
        <Stack gap="xs">
          <DetailRow label="Lead capture" value="Unlimited" ruled />
          <DetailRow
            label={`Bulk enrichment • ${formatUsd(CREDIT_PRICING.unlimitedOverageUsdPerCredit)} per credit`}
            value={formatCreditCount(bulkEnrichmentMilli)}
          />
        </Stack>
      </Stack>
      <ChargeBlock
        label="Estimated additional charge"
        amountUsd={
          toCredits(summary.estimatedBillMilli) * CREDIT_PRICING.unlimitedOverageUsdPerCredit
        }
        caption={ADDITIONAL_CHARGE_CAPTION}
      />
    </PlanCardShell>
  );
}

function PaygCard({ summary }: { summary: CreditSummary }) {
  return (
    <PlanCardShell>
      <Stack gap={2}>
        <Text fw={600} lh="20px">
          Pay as you go
        </Text>
        <DetailText>No credit plan</DetailText>
      </Stack>
      <Box>
        <StatRow
          label="Credits used"
          value={formatCreditCount(summary.consumedThisMonthMilli)}
          emphasis
        />
      </Box>
      <ChargeBlock
        label="Estimated charge"
        amountUsd={toCredits(summary.consumedThisMonthMilli) * CREDIT_PRICING.paygUsdPerCredit}
        caption="Billed at month end"
      />
    </PlanCardShell>
  );
}

export function CreditUsagePlanCard({
  planKind,
  summary,
  purchasedMilli,
  consumedFromPacksMilli,
  availableMilli,
  renewalDate,
}: CreditUsagePlanCardProps) {
  if (planKind === 'CREDIT_PACKS') {
    return (
      <CreditPacksCard
        summary={summary}
        purchasedMilli={purchasedMilli}
        consumedFromPacksMilli={consumedFromPacksMilli}
        availableMilli={availableMilli}
        renewalDate={renewalDate}
      />
    );
  }

  if (planKind === 'UNLIMITED') {
    return (
      <UnlimitedCard summary={summary} purchasedMilli={purchasedMilli} renewalDate={renewalDate} />
    );
  }

  return <PaygCard summary={summary} />;
}
